import React, { useEffect, useState } from 'react'
import Footer from '../components/Footer'
import { FaFacebookF } from 'react-icons/fa'
import { AiOutlineGoogle } from 'react-icons/ai'
import FadeLoader from 'react-spinners/FadeLoader'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { mode } from '../utils/config'
import { customer_register, messageClear } from '../store/reducers/authReducer'

const Register = () => {
  const navigate = useNavigate()
  const { loader, successMessage, errorMessage, userInfo } = useSelector(
    (state) => state.auth
  )
  const dispatch = useDispatch()

  const [state, setState] = useState({
    name: '',
    email: '',
    password: ''
  })

  // Handle input changes
  const inputHandle = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value
    })
  }

  // Form submission
  const register = (e) => {
    e.preventDefault()
    dispatch(customer_register(state))
  }

  // Toast and redirect logic
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage)
      dispatch(messageClear())
    }
    if (errorMessage) {
      toast.error(errorMessage)
      dispatch(messageClear())
    }
    if (userInfo) {
      navigate('/')
    }
  }, [successMessage, errorMessage, userInfo, navigate, dispatch])

  return (
    <div className="relative min-h-screen flex flex-col bg-gray-900 overflow-hidden">
      {/* Loader Overlay */}
      {loader && (
        <div className="fixed inset-0 z-[999] flex justify-center items-center bg-[#38303033]">
          <FadeLoader />
        </div>
      )}

      {/* Top Wave (same gradients as Login) */}
      <div className="absolute top-0 left-0 right-0 overflow-hidden leading-[0] rotate-180">
        <svg
          className="block w-[140%] min-w-[900px]"
          viewBox="0 0 1920 320"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="url(#gradientTop)"
            fillOpacity="0.6"
            d="M0,288L48,272C96,256,192,224,288,224C384,224,480,256,576,224C672,192,768,96,864,80C960,64,1056,128,1152,170.7C1248,213,1344,235,1440,224C1536,213,1632,171,1728,160C1824,149,1920,171,1920,181.3L1920,320L1824,320C1728,320,1632,320,1536,320C1440,320,1344,320,1248,320C1152,320,1056,320,960,320C864,320,768,320,672,320C576,320,480,320,384,320C288,320,192,320,96,320L0,320Z"
          />
          <defs>
            <linearGradient
              id="gradientTop"
              x1="0"
              y1="0"
              x2="0"
              y2="320"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#8E2DE2" />
              <stop offset="1" stopColor="#4A00E0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Bottom Wave (same gradients as Login) */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-[0]">
        <svg
          className="block w-[140%] min-w-[900px]"
          viewBox="0 0 1920 320"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="url(#gradientBottom)"
            fillOpacity="0.6"
            d="M0,256L60,234.7C120,213,240,171,360,165.3C480,160,600,192,720,224C840,256,960,288,1080,282.7C1200,277,1320,235,1440,224C1560,213,1680,235,1800,234.7C1920,235,1920,235,1920,235L1920,320L1800,320C1680,320,1560,320,1440,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
          />
          <defs>
            <linearGradient
              id="gradientBottom"
              x1="0"
              y1="0"
              x2="0"
              y2="320"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#8E2DE2" />
              <stop offset="1" stopColor="#4A00E0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-10 relative z-10">
        {/* Glassmorphic Card */}
        <div className="w-full max-w-md bg-white/10 backdrop-blur-md border border-white/20 
                        rounded-3xl shadow-2xl p-6 sm:p-8 text-white">
          {/* Logo on top, same as Login */}
          <div className="flex justify-center mb-4">
            <img
              className="rounded-full w-32 h-32 object-cover shadow-md"
              src="/images/logo2.png"
              alt="Trend Street Logo"
            />
          </div>

          <h2 className="text-center text-2xl sm:text-3xl font-bold mb-6">Register</h2>

          {/* Registration Form */}
          <form onSubmit={register} className="space-y-4 text-slate-200">
            <div>
              <label htmlFor="name" className="block mb-1 font-semibold">
                Name
              </label>
              <input
                onChange={inputHandle}
                value={state.name}
                type="text"
                className="w-full px-3 py-2 rounded-md border border-purple-300 
                           bg-transparent focus:outline-none focus:ring-2 
                           focus:ring-purple-400 transition-colors"
                id="name"
                name="name"
                placeholder="Enter your name"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block mb-1 font-semibold">
                Email
              </label>
              <input
                onChange={inputHandle}
                value={state.email}
                type="email"
                className="w-full px-3 py-2 rounded-md border border-purple-300 
                           bg-transparent focus:outline-none focus:ring-2 
                           focus:ring-purple-400 transition-colors"
                id="email"
                name="email"
                placeholder="Enter your email"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block mb-1 font-semibold">
                Password
              </label>
              <input
                onChange={inputHandle}
                value={state.password}
                type="password"
                className="w-full px-3 py-2 rounded-md border border-purple-300 
                           bg-transparent focus:outline-none focus:ring-2 
                           focus:ring-purple-400 transition-colors"
                id="password"
                name="password"
                placeholder="Create a password"
                required
              />
            </div>
            <button
              className="w-full py-2 mt-4 rounded-md bg-purple-600 hover:bg-purple-700 
                         text-white font-semibold shadow-lg transition-all 
                         duration-200 outline-none"
            >
              Register
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-6">
            <hr className="flex-1 border-purple-300/50" />
            <span className="px-2 text-purple-200">or</span>
            <hr className="flex-1 border-purple-300/50" />
          </div>

          {/* Social Buttons */}
          <button
            className="w-full py-2 mb-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white 
                       flex items-center justify-center gap-2 font-semibold shadow-md transition"
          >
            <FaFacebookF />
            <span>Register with Facebook</span>
          </button>
          <button
            className="w-full py-2 rounded-md bg-red-600 hover:bg-red-700 text-white 
                       flex items-center justify-center gap-2 font-semibold shadow-md transition"
          >
            <AiOutlineGoogle />
            <span>Register with Google</span>
          </button>

          {/* Links */}
          <div className="text-center mt-4 text-sm text-purple-200">
            <p>
              Already have an account?{' '}
              <Link className="text-purple-100 font-semibold hover:text-white" to="/login">
                Login
              </Link>
            </p>
            <p className="pt-1">
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-100 font-semibold hover:text-white"
                href={
                  mode === 'dev'
                    ? 'http://localhost:3001/login'
                    : 'https://hawkers-dashboard.vercel.app/login'
                }
              >
                Login seller account
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default Register
